<template>
  <Component
    v-bind="$attrs"
    :is="is"
    :type="type"
    :href="href"
    :rel="rel"
    :class="[size, color]"
    :disabled="localDisabled"
    class="inline-flex items-center overflow-hidden rounded-full transition-all duration-300 ease-in-out disabled:pointer-events-none"
  >
    <span
      class="pointer-events-none flex flex-1 select-none items-center gap-x-1"
    >
      <slot name="prefix" />
      <span class="flex flex-1 items-center justify-center">
        <span class="relative flex items-center">
          <span
            class="absolute left-0 block opacity-0 transition-all duration-300 ease-in-out"
            :class="{
              'opacity-100': lazyLoading,
              '-translate-x-2': lazyLoading && props.size === 'xs',
              '-translate-x-3': lazyLoading && props.size !== 'xs',
            }"
          >
            <BaseLoading
              v-if="lazyLoading"
              :class="[text]"
              :width="loadingIconDiameter"
              :height="loadingIconDiameter"
            />
          </span>
          <span
            class="transition-all duration-300 ease-in-out"
            :class="{
              'translate-x-2': lazyLoading && props.size === 'xs',
              'translate-x-3': lazyLoading && props.size !== 'xs',
            }"
            ><span
              :class="[
                'whitespace-nowrap transition-all duration-300 ease-in-out',
                text,
              ]"
              ><slot /></span
          ></span>
        </span>
      </span>
    </span>
  </Component>
</template>

<script setup lang="ts">
const props = defineProps({
  tag: {
    type: String,
    default: 'button',
    validator: (v: string) => {
      return ['button', 'nuxt-link', 'a'].includes(v)
    },
  },
  theme: {
    type: String,
    default: 'default',
    validator: (v: string) => {
      return ['default', 'primary', 'secondary', 'text', 'custom'].includes(v)
    },
  },
  size: {
    type: String,
    default: 'base',
    validator: (v: string) => {
      return ['xs', 'sm', 'base', 'lg'].includes(v)
    },
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  href: {
    type: String,
    default: undefined,
  },
  type: {
    type: String,
    default: undefined,
  },
})

const slots = useSlots()

const hasPrefixSlot = computed(() => {
  return !!(slots.prefix && slots.prefix().length > 0)
})

// attributes

const is = computed(() => {
  if (props.tag === 'nuxt-link') {
    return resolveComponent('NuxtLink')
  }
  return props.tag
})

const externalLink = computed(() => {
  if (!props.href) return undefined
  return props.href.startsWith('http')
})

const rel = computed(() => {
  if ((props.tag === 'nuxt-link' || props.tag === 'a') && externalLink.value) {
    return 'noopener noreferrer'
  }
  return undefined
})

const type = computed(() => {
  if (props.type) {
    return props.type
  }
  if (props.tag === 'button') {
    return 'button'
  }
  return undefined
})

const localDisabled = computed(() => {
  return props.disabled || props.loading
})

// styles

const size = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h-[28px] px-4 text-sm-b'
    case 'sm':
      return `h-[36px] pr-5 text-base-b ${hasPrefixSlot.value ? 'pl-3' : 'pl-5'}`
    case 'lg':
      return 'h-[52px] px-8 text-lg-b'
    case 'base':
    default:
      return 'h-[44px] px-6 text-lg-b'
  }
})

const color = computed(() => {
  if (!props.disabled || props.loading) {
    switch (props.theme) {
      case 'primary':
        return 'bg-primary hover:bg-red-50 focus:outline-offset-[3px] focus:outline-black focus:outline-2'
      case 'secondary':
        return 'border border-primary bg-white hover:bg-red-background focus:outline-black focus:outline-2'
      case 'text':
        return 'hover:opacity-70 focus:outline-black focus:outline-2'
      case 'custom':
        return ''
      case 'default':
      default:
        return 'border border-grey-20 bg-white hover:bg-grey-background focus:outline-black focus:outline-2'
    }
  } else {
    switch (props.theme) {
      case 'primary':
        return 'bg-grey-20'
      case 'secondary':
        return 'border-grey-20 bg-white border border-grey-20'
      case 'text':
        return ''
      case 'default':
      default:
        return 'border-grey-20 bg-white border border-grey-20'
    }
  }
})

const text = computed(() => {
  if (!props.disabled || props.loading) {
    switch (props.theme) {
      case 'primary':
        return 'text-white'
      case 'secondary':
      case 'text':
        return 'text-primary '
      case 'custom':
        return ''
      case 'default':
      default:
        return 'text-black'
    }
  } else {
    switch (props.theme) {
      case 'primary':
        return 'text-white'
      case 'secondary':
        return 'text-grey-20'
      case 'default':
      default:
        return 'text-grey-20'
    }
  }
})

const loadingIconDiameter = computed(() => {
  switch (props.size) {
    case 'xs':
      return 12
    default:
      return 16
  }
})

const lazyLoading = ref(false)
const lazyLoadingTimeout = ref<any>()

watch(
  () => props.loading,
  (value) => {
    if (value) {
      lazyLoadingTimeout.value = setTimeout(() => {
        lazyLoading.value = true
      }, 500)
    } else {
      clearTimeout(lazyLoadingTimeout.value)
      lazyLoading.value = false
    }
  },
)
</script>
